import React, {useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import {
    RepeatedAskGroupDTO,
    RepeatedAskMemberDTO,
    RepeatedAskOrder,
    SafetyRecommendationLetterDTO,
    SafetyRecommendationStatusGroup,
} from '../../@types/safety-recommendation';
import {RecommendationStatusChip} from './RecommendationOverview';
import CorrespondenceList from './CorrespondenceList';
import {downloadCsvFile, rowsToCsv} from '../../utils/export/csv';

/**
 * The same ask, made again over the years, and what became of each attempt.
 *
 * <p>A group is a row rather than a list: the title says what is being asked for, and the
 * recommendations behind it open only when a reader wants them. Six hundred groups of three or four
 * recommendations each is unreadable any other way.</p>
 */

const STATUSES_NOT_ACTED_ON: SafetyRecommendationStatusGroup[] =
    ['CLOSED_UNACCEPTABLE', 'OPEN', 'OPEN_UNACCEPTABLE'];

const ORDER_LABELS: Record<RepeatedAskOrder, string> = {
    NEWEST: 'Asked again most recently',
    LONGEST_RUNNING: 'Longest running',
    MOST_REPEATED: 'Most repeated',
};

export interface RepeatedAsksProps {
    groups: RepeatedAskGroupDTO[] | null;
    order: RepeatedAskOrder;
    onOrderChange: (order: RepeatedAskOrder) => void;
    /** Fetches every group for the export, which the panel itself does not hold. */
    onExport?: () => Promise<RepeatedAskGroupDTO[]>;
    /**
     * The letters for one recommendation, fetched when a reader opens it.
     *
     * <p>There is no public page to send them to: the NTSB's per-recommendation URL is long dead and
     * its replacement renders client-side, so a link out would be a link to an empty page. The
     * recommendation is shown here instead, in full, with its correspondence.</p>
     */
    onLoadLetters?: (member: RepeatedAskMemberDTO) => Promise<SafetyRecommendationLetterDTO[]>;
}

/** The year an ask was made, or nothing when the source never dated it. */
export const yearOf = (issued: string | null | undefined): string => (issued ? issued.slice(0, 4) : '');

/** How a group's run reads: the years it spans, and how much of it went unheeded. */
export const runOf = (group: RepeatedAskGroupDTO): string => {
    const times = `asked ${group.members.length} time${group.members.length === 1 ? '' : 's'}`;
    const years = group.spanYears > 0 ? ` over ${group.spanYears} years` : ' in one year';
    return `${times}${years}, ${group.notActedOn} not acted on`;
};

/** One row per recommendation, carrying its group's details, which is what a spreadsheet wants. */
export const toCsv = (groups: RepeatedAskGroupDTO[]): string => rowsToCsv(
    ['Repeated ask', 'First issued', 'Last issued', 'Span (years)', 'Times asked', 'Not acted on',
        'Issuer confirms', 'Recommendation', 'Issued', 'Status', 'Text'],
    groups.flatMap((group) => group.members.map((member) => [
        group.title,
        group.firstIssued ?? '',
        group.lastIssued ?? '',
        group.spanYears,
        group.members.length,
        group.notActedOn,
        group.markedByIssuer ? 'yes' : 'no',
        member.number,
        member.issued ?? '',
        member.statusGroup,
        member.text,
    ])),
);

const RepeatedAsks: React.FC<RepeatedAsksProps> = ({groups, order, onOrderChange, onExport, onLoadLetters}) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [openRecommendations, setOpenRecommendations] = useState<Record<number, boolean>>({});
    const [letters, setLetters] = useState<Record<number, SafetyRecommendationLetterDTO[] | null>>({});
    const [letterErrors, setLetterErrors] = useState<Record<number, string>>({});
    const [exporting, setExporting] = useState(false);

    /** Opens one recommendation in place, fetching its letters the first time it is opened. */
    const toggleRecommendation = (member: RepeatedAskMemberDTO): void => {
        const opening = !openRecommendations[member.id];
        setOpenRecommendations((was) => ({...was, [member.id]: opening}));
        if (!opening || !onLoadLetters || letters[member.id] !== undefined) {
            return;
        }
        setLetters((was) => ({...was, [member.id]: null}));
        onLoadLetters(member)
            .then((found): void => setLetters((was) => ({...was, [member.id]: found})))
            .catch((failure: Error): void => {
                setLetterErrors((was) => ({...was, [member.id]: failure.message || 'Failed to load the letters'}));
                setLetters((was) => ({...was, [member.id]: []}));
            });
    };

    const exportAll = (): void => {
        if (!onExport) return;
        setExporting(true);
        onExport()
            .then((all): void => {
                downloadCsvFile(`repeated-asks-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(all));
            })
            .finally((): void => setExporting(false));
    };

    const controls = (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{pb: 1}}>
            <FormControl size="small" sx={{minWidth: 220}}>
                <InputLabel id="repeated-ask-order">Order by</InputLabel>
                <Select
                    labelId="repeated-ask-order"
                    label="Order by"
                    value={order}
                    onChange={(event): void => onOrderChange(event.target.value as RepeatedAskOrder)}
                >
                    {(Object.keys(ORDER_LABELS) as RepeatedAskOrder[]).map((option) => (
                        <MenuItem key={option} value={option}>{ORDER_LABELS[option]}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            {onExport && (
                <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportAll}
                    disabled={exporting || groups === null}
                >
                    {exporting ? 'Preparing' : 'Export CSV'}
                </Button>
            )}
        </Stack>
    );

    if (groups === null) {
        return (
            <Box>
                {controls}
                <Typography variant="body2" color="text.secondary" sx={{p: 2}}>Loading.</Typography>
            </Box>
        );
    }
    if (groups.length === 0) {
        return (
            <Box>
                {controls}
                <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                    No repeated asks yet. They are worked out from the meaning-search index, so they appear once a
                    source has been indexed.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {controls}
            <Stack divider={<Divider />} spacing={0}>
                {groups.map((group) => {
                    const open = Boolean(expanded[group.key]);
                    return (
                        <Box key={group.key} sx={{py: 1.25}}>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                                sx={{cursor: 'pointer'}}
                                onClick={(): void => setExpanded((was) => ({...was, [group.key]: !was[group.key]}))}
                                role="button"
                                aria-expanded={open}
                                aria-label={`${group.title}, ${runOf(group)}`}
                            >
                                <ExpandMoreIcon
                                    fontSize="small"
                                    sx={{
                                        mt: 0.25,
                                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                                        transition: 'transform 150ms',
                                    }}
                                />
                                <Box sx={{flex: 1}}>
                                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>{group.title}</Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="body2" color="text.secondary">
                                            {yearOf(group.firstIssued)} to {yearOf(group.lastIssued)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">{runOf(group)}</Typography>
                                        {group.markedByIssuer && (
                                            <Tooltip
                                                title="The issuer marked one of these as superseding another, so it agrees these are the same ask"
                                                describeChild
                                            >
                                                <Chip size="small" label="Issuer confirms" variant="outlined" color="info" />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                            <Collapse in={open} unmountOnExit>
                                <Stack spacing={0.75} sx={{mt: 1, ml: 3.5}}>
                                    {group.members.map((member) => {
                                        const showing = Boolean(openRecommendations[member.id]);
                                        return (
                                            <Box key={member.id}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="flex-start"
                                                    sx={{borderRadius: 1, px: 0.5, py: 0.25}}
                                                >
                                                    <Typography variant="body2" sx={{minWidth: 44}} color="text.secondary">
                                                        {yearOf(member.issued)}
                                                    </Typography>
                                                    {/* The number opens the recommendation in place; the rest of
                                                        the row is text a reader may want to select. */}
                                                    <Link
                                                        component="button"
                                                        variant="body2"
                                                        underline="hover"
                                                        sx={{minWidth: 92, fontWeight: 500, textAlign: 'left'}}
                                                        aria-expanded={showing}
                                                        onClick={(): void => toggleRecommendation(member)}
                                                    >
                                                        {member.number}
                                                    </Link>
                                                    <RecommendationStatusChip
                                                        group={member.statusGroup}
                                                        label={STATUSES_NOT_ACTED_ON.includes(member.statusGroup)
                                                            ? 'Not acted on' : 'Acted on'}
                                                        ariaLabel={`${member.number} ${member.statusGroup}`}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={showing ? undefined : {
                                                            display: '-webkit-box', WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                        }}
                                                    >
                                                        {member.text}
                                                    </Typography>
                                                </Stack>
                                                <Collapse in={showing} unmountOnExit>
                                                    <Box sx={{ml: 6.5, mt: 0.5, mb: 1}}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Correspondence
                                                        </Typography>
                                                        <CorrespondenceList
                                                            letters={letters[member.id] ?? null}
                                                            error={letterErrors[member.id] ?? null}
                                                            group={member.statusGroup}
                                                        />
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Collapse>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default RepeatedAsks;
